import {
  List as ChakraUIList,
  ListProps as ChakraUIListProps,
  OrderedList as ChakraUIOrderedList,
  UnorderedList as ChakraUIUnorderedList,
  ListItem as ChakraUIListItem,
  ListItemProps as ChakraUIListItemProps,
  ListIcon,
  Stack,
  HStack,
  Heading,
  Text,
  Box,
  Flex,
  Spacer,
  AspectRatio
} from "@chakra-ui/react";

import { ReactNode } from "react";

import { Spinner } from "../spinner/spinner";

export interface ListProps
  extends Pick<
    ChakraUIListProps,
    "spacing" | "children" | "styleType" | "stylePosition" | "id" | "className"
  > {
  loadMore?: ReactNode;
  isLoading?: boolean;
  title?: string;
  description?: string;
  isBordered?: boolean;
}

export interface ListItemProps extends Pick<ChakraUIListItemProps, "children"> {
  cover?: ReactNode;
  body?: ReactNode;
  leftActions?: Array<ReactNode>;
  rightActions?: Array<ReactNode>;
}

const borderedStyles = {
  borderRadius: "0.5rem",
  border: "1px solid #E2E8F0",
  padding: { base: "1.5rem 1rem", lg: "2.5rem" },
  width: "100%"
};

const ListHead = ({
  title,
  description
}: Pick<ListProps, "title" | "description">) => {
  if (!title && !description) return null;

  return (
    <Box mb={10}>
      {title && <Heading size="md">{title}</Heading>}
      {description && <Text mt={2}>{description}</Text>}
    </Box>
  );
};

export function List(props: ListProps) {
  const {
    loadMore,
    isLoading,
    children,
    title,
    description,
    isBordered,
    ...rest
  } = props;

  if (isLoading) {
    return (
      <Spinner>
        <ChakraUIList
          {...rest}
          {...(isBordered && borderedStyles)}
          styleType="none"
        >
          <ListHead title={title} description={description} />
          {children}
          {!!loadMore && <Box>{loadMore}</Box>}
        </ChakraUIList>
      </Spinner>
    );
  }

  return (
    <ChakraUIList
      {...rest}
      {...(isBordered && borderedStyles)}
      styleType="none"
    >
      <ListHead title={title} description={description} />
      {children}
      {!!loadMore && <Box>{loadMore}</Box>}
    </ChakraUIList>
  );
}

export function OrderedList(props: ListProps) {
  const {
    loadMore,
    isLoading,
    children,
    title,
    description,
    isBordered,
    ...rest
  } = props;

  if (isLoading) {
    return (
      <Spinner>
        <ChakraUIOrderedList {...rest} {...(isBordered && borderedStyles)}>
          <ListHead title={title} description={description} />
          {children}
          {!!loadMore && <Box>{loadMore}</Box>}
        </ChakraUIOrderedList>
      </Spinner>
    );
  }

  return (
    <ChakraUIOrderedList {...rest} {...(isBordered && borderedStyles)}>
      <ListHead title={title} description={description} />
      {children}
      {!!loadMore && <Box>{loadMore}</Box>}
    </ChakraUIOrderedList>
  );
}

export function UnorderedList(props: ListProps) {
  const {
    loadMore,
    isLoading,
    children,
    title,
    description,
    isBordered,
    ...rest
  } = props;

  if (isLoading) {
    return (
      <Spinner>
        <ChakraUIUnorderedList {...rest} {...(isBordered && borderedStyles)}>
          <ListHead title={title} description={description} />
          {children}
          {!!loadMore && <Box>{loadMore}</Box>}
        </ChakraUIUnorderedList>
      </Spinner>
    );
  }

  return (
    <ChakraUIUnorderedList {...rest} {...(isBordered && borderedStyles)}>
      <ListHead title={title} description={description} />
      {children}
      {!!loadMore && <Box>{loadMore}</Box>}
    </ChakraUIUnorderedList>
  );
}

export function ListItem(props: ListItemProps) {
  const { cover, body, leftActions, rightActions, ...rest } = props;

  if (cover || body || leftActions || rightActions) {
    return (
      <ChakraUIListItem {...rest}>
        <Stack direction={{ base: "column", md: "row" }} spacing={4}>
          {cover && (
            <AspectRatio
              minWidth={{ md: 36 }}
              ratio={{ base: 16 / 9, lg: 4 / 3 }}
              sx={{ "> img": { borderRadius: "lg" } }}
            >
              {cover}
            </AspectRatio>
          )}
          {(body || leftActions || rightActions) && (
            <Stack width="100%" spacing={4}>
              <Box sx={{ "> p": { fontSize: "md" } }}>{body}</Box>
              <Flex
                gap={2}
                align="flex-start"
                sx={{ "> *": { fontSize: "xs", fontWeight: "medium" } }}
              >
                {leftActions && (
                  <>
                    <Flex rowGap={2} columnGap={4}>
                      {leftActions.map((action, index) => (
                        <HStack
                          spacing={0}
                          key={`leftAction-${index}`}
                          sx={{
                            "*": { maxHeight: 4 },
                            svg: { width: 4, height: 4 }
                          }}
                        >
                          {action}
                        </HStack>
                      ))}
                    </Flex>
                    <Spacer />
                  </>
                )}
                {rightActions && (
                  <Flex
                    wrap="wrap"
                    rowGap={2}
                    columnGap={4}
                    justify="flex-end"
                    sx={{ ">[role='button']": { cursor: "pointer" } }}
                  >
                    {rightActions.map((action, index) => (
                      <HStack
                        spacing={0}
                        key={`rightAction-${index}`}
                        sx={{
                          "*": { maxHeight: 4 },
                          svg: { width: 4, height: 4 }
                        }}
                      >
                        {action}
                      </HStack>
                    ))}
                  </Flex>
                )}
              </Flex>
            </Stack>
          )}
        </Stack>
      </ChakraUIListItem>
    );
  }

  return <ChakraUIListItem {...rest} />;
}

export { ListIcon };
